import type { BasicFilter, DataSchema, DialogActionFormItemBehavior, FilterValue, FormItem, MappedFilterValue, ThreeLevelsFilterValue } from 'types';

import type { JoinedListingNestedIdentity } from './table';
import type {
  AutomaticSequenceAttribute,
  ButtonAttribute,
  CheckboxAttribute,
  CommentAttribute,
  ComputedAttribute,
  DateAttribute,
  EmailAttribute,
  FileUploadAttribute,
  FormItemWithoutVisibilities,
  NumberAttribute,
  PhoneNumberAttribute,
  RadioButtonAttribute,
  RecordsAttribute,
  RichTextAttribute,
  TablesAttribute,
  TextAttribute,
  UsersAttribute,
  RecordsDisplayAttribute,
} from './recordAttribute';
import type { VisibilityState } from './views';
import type { FormItemUiDefinition } from './formItem';
import type { getAttributeForSave } from '../utils/agendaRecord/agendaRecord';
import type { IconNames } from 'components/mui/common/Icon';

export type AttributeType = ReturnType<typeof getAttributeForSave>;

export type UserResponse = {
  userId: string;
  fullName: string;
  realUserId?: string;
  realUserFullName?: string;
};

export interface GetAgendaRecordsReq<T> extends BasicFilter<T> {
  agendaId: string;
  viewId?: string;
  formItemId?: string;
}

export interface GetValueFormItems {
  viewFormItemId: string;
  valueDirect: string[] | null;
  valueSourceType: 'CreatedDate' | 'UpdatedDate' | 'DateNow' | 'UserId' | 'UserRole' | 'UserEmail' | 'FormItem' | null;
  valueFormItemId: string | null;
  valueRecordIds?: string[];
}

export type GetAgendaRecordsRes<T> = {
  items: T;
  page: number;
  pageSize: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  displayValuesMetadata?: DisplayValuesMetadata | null;
};

export interface DisplayValuesMetadata {
  omitted: boolean;
  reason: 'RecordCountLimitExceeded';
  limit: number;
  recordCount: number;
  omittedSourceFormItemIds: string[];
}

export interface GetAgendaRecordReq {
  params: GetAgendaRecordsReq<DataSchema>;
  valueFormItems: GetValueFormItems[];
  recordIds: string[];
  skipViewConditions?: boolean;
  evaluateVisualAttributes?: boolean;
  filters: FilterValue[];
}

export interface GetAgendaFilteredRecordReq extends Omit<GetAgendaRecordReq, 'filters' | 'recordIds'> {
  filters: ThreeLevelsFilterValue[] | MappedFilterValue[];
}

export enum HeartbeatReason {
  Editing = 'Editing',
  UploadingFiles = 'UploadingFiles',
}

export interface RecordLockStateResponse {
  isLocked: boolean;
  lockedBy: string | null;
  lockedAtUtc: string | null;
  expiresAtUtc: string | null;
}

export interface AcquireRecordLockResponse {
  success: boolean;
  lockToken: string | null;
  lockedBy: string | null;
  expiresAtUtc: string | null;
}

export interface HeartbeatRecordLockResponse {
  isValid: boolean;
  extended: boolean;
  expiresAtUtc: string | null;
  invalidReason: string | null;
}

export interface GetAgendaRecordRes {
  tabs: {
    sections: RecordSection[];
    tabNumber: number;
  }[];
  record: {
    id: string;
    identifier: string;
    agendaId: string;
    moduleId: string;
    systemType: string;
    createdBy: UserResponse;
    createdAt: Date;
    lastUpdatedBy: UserResponse;
    lastUpdatedAt: Date;
    currentVersionId: string;
    state: string;
    status?: string;
  };
  lockState: RecordLockStateResponse | null;
}

export interface JoinedFilteredAgendaRes {
  mainRecord: AgendaRecordItem;
  nestedRecord: AgendaRecordItem | null;
  secondLevelNestedRecord: AgendaRecordItem | null;
}

export interface AgendaRecordItem extends JoinedListingNestedIdentity {
  id: string;
  agendaId: string;
  createdBy: UserResponse;
  state: VisibilityState;
  status?: string;
  createdAt: Date;
  lastUpdatedBy: string;
  lastUpdatedAt: Date;
  currentVersionId: string;
  attributes: RecordAttributeRes[];
}

export type JoinedRecordRef = {
  id: string;
  agendaId: string;
};

export type FlattenedJoinedAgendaRecord = AgendaRecordItem & {
  rowId: string;
  nestedRecord: JoinedRecordRef | null;
  secondLevelNestedRecord: JoinedRecordRef | null;
};

export interface CalculateAttribute extends Omit<RecordAttributeRes, 'id' | 'visibilityResult'> {
  sectionId: string;
  visibility?: VisibilityResult;
}

export interface CalculateRes {
  attributes: CalculateAttribute[];
}

export interface RecordAttributeRes extends RecordAttribute {
  id: string;
  formItem: FormItemWithoutVisibilities | null;
  state: VisibilityState | null;
  visibilityResult: VisibilityResult;
}

export interface VisibilityResult {
  visibilityState: VisibilityState;
  formItemUiDefinition: FormItemUiDefinition | null;
  multipleUiDefinitions: boolean | null;
}

export interface RecordAttribute {
  sectionId?: string;
  subSectionId?: string;
  automaticSequenceAttribute: AutomaticSequenceAttribute | null;
  buttonAttribute: ButtonAttribute | null;
  checkboxAttribute: CheckboxAttribute | null;
  commentAttribute: CommentAttribute | null;
  computedAttribute: ComputedAttribute | null;
  dateAttribute: DateAttribute | null;
  emailAttribute: EmailAttribute | null;
  fileUploadAttribute: FileUploadAttribute | null;
  numberAttribute: NumberAttribute | null;
  phoneNumberAttribute: PhoneNumberAttribute | null;
  radioButtonAttribute: RadioButtonAttribute | null;
  recordsAttribute: RecordsAttribute | null;
  recordsDisplayAttribute: RecordsDisplayAttribute | null;
  richTextAttribute: RichTextAttribute | null;
  tablesAttribute: TablesAttribute | null;
  textAttribute: TextAttribute | null;
  usersAttribute: UsersAttribute | null;
}

export interface RecordTableItem {
  rowId: string;
  cellId: string;
  text: InnerItem | null;
  email: InnerItem | null;
  phoneNumber: InnerItem | null;
  number: InnerItem | null;
  reference: InnerItem | null;
}

interface InnerItem {
  value: string;
}

export interface RecordSection {
  id: string;
  name: string;
  description?: string;
  collapsed: boolean;
  gridSizeX: number;
  gridSizeY: number;
  formItems: RecordFormItem[];
  formItemTabs: RecordFormItemTab[];
}

export interface RecordFormItemTab {
  id: string;
  name: string | null;
  tabNumber: number;
  formItems: RecordFormItem[];
}

export interface RecordFormItem {
  formItem: FormItemWithoutVisibilities;
  visibilityState: VisibilityState;
  attribute: RecordAttribute | null;
  visibilityFormItemUiDefinition?: FormItemUiDefinition | null;
  visibilityMultipleUiDefinitions: boolean;
}

export interface AgendaRecordVersions {
  id: string;
  agendaId: string;
  createdBy: UserResponse;
  state: string;
  createdAt: string;
  lastUpdatedBy: UserResponse;
  lastUpdatedAt: string;
  currentVersionId: string;
  versions: GetAgendaRecordsRes<Versions[]>;
}

export interface Versions {
  versionId: string;
  isMajorVersion: boolean;
  modifiedBy: UserResponse;
  realUser?: UserResponse;
  modificationTimestamp: string;
}

export interface UpdateAgendaRecordRes {
  recordId: string;
  redirectAction?: {
    otherRecordId?: string;
    otherAgendaId?: string;
    otherModuleId?: string;
    otherViewId?: string;
  };
  deleteRecordAction?: {
    deletedRecords?: string[];
  };
  redirectExternalAction?: {
    link: string;
  };
  dialogAction?: {
    icon?: IconNames;
    title?: string;
    description?: string;
    actionableFormItems?: { formItem: FormItem; behavior: DialogActionFormItemBehavior }[];
  };
  message?: {
    message: string;
  };
}

export interface NestedListData {
  agendaId: string;
  formItemId: string;
  recordId: string | null;
  attributes: AttributeType[];
}

export interface UpdateWithNestedListReq {
  agendaId: string;
  lockToken?: string;
  body: {
    recordId: string | null;
    attributes: AttributeType[];
    nestedListData?: NestedListData[];
  };
}

export interface UpdateWithNestedListRes {
  mainRecord: UpdateAgendaRecordRes;
  nestedRecords: UpdateAgendaRecordRes[];
}

export interface RecordAttributeLightRes extends RecordAttribute {
  id: string;
  formItemId: string;
  visibilityResult: VisibilityResult;
}

export interface AgendaRecordLightItem {
  id: string;
  agendaId: string;
  createdBy: UserResponse;
  state: VisibilityState;
  status?: string;
  createdAt: Date;
  lastUpdatedBy: string;
  lastUpdatedAt: Date;
  currentVersionId: string;
  attributes: RecordAttributeLightRes[];
  realCreatedBy?: UserResponse;
  realLastUpdatedBy?: UserResponse;
}

export type RecordsForViewLightRes = {
  formItemDefinitions: Record<string, FormItemWithoutVisibilities>;
  records: GetAgendaRecordsRes<AgendaRecordLightItem[]>;
  displayValuesMetadata?: DisplayValuesMetadata | null;
};
